import { useEffect, useRef, useState } from "react"
import { prefersReducedMotion } from "../../landing/motion"

/**
 * True once the element has scrolled into view (and stays true: each reveal plays once).
 * With reduced motion, or where IntersectionObserver is missing, it is true from the first render,
 * so the final state shows and nothing moves.
 */
export function useReveal<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null)
  const still = prefersReducedMotion() || typeof IntersectionObserver === "undefined"
  const [seen, setSeen] = useState(still)
  useEffect(() => {
    if (seen || !ref.current) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect() }
    }, { threshold: 0.2 })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [seen])
  return [ref, seen]
}

/** True while at least `threshold` of the element is on screen. Always true where IntersectionObserver is missing. */
export function useInView<T extends HTMLElement>(threshold = 0.2): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(typeof IntersectionObserver === "undefined")
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined" || !ref.current) return
    const io = new IntersectionObserver((entries) => {
      const last = entries[entries.length - 1]
      if (last) setInView(last.isIntersecting)
    }, { threshold })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [threshold])
  return [ref, inView]
}
